import { PageHeader } from "../shell/PageHeader";
import {
  useGetV1Alarms,
  useGetV1Home,
  useGetV1Plants,
  type Action,
  type Figure,
  type LossCascadeRow,
  type RowStatus,
} from "@solarkey/api-client";
import {
  AttainmentBar,
  CoverageBar,
  EmptyState,
  ErrorState,
  HealthBadge,

  Metric,
  Money,
  Skeleton,
  StateWord,
  type HealthModifier,
  type HealthState,
  atDisplayScale,
} from "@solarkey/ui";
import { LossCascade, type CascadeRowStatus, type CascadeStep } from "@solarkey/charts";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";

import { useCapabilities, useEntitlement } from "../session/capabilities";
import { HomeEasyM } from "./HomeEasyM";

/* Easy M is a TIER, not a role, so the SERVER decides this composition: `home.py`
   emits composition "M" for an Easy M tenant and the client honours it. The old
   HomeEasyM screen was deleted because nothing could reach it and it printed the
   attainment ratio under the label "PR"; this one is reached by tier and reads the
   tiles the view-model now serves. */
type CompositionRole =
  | "easy_m"
  | "executive"
  | "asset_manager"
  | "regional_ops"
  | "control_room"
  | "site"
  | "technician"
  | "contractor"
  | "viewer"
  | "ops";

interface CompositionProps {
  h: any;
  period?: string;
  from?: string;
  to?: string;
  hasValueLens: boolean;
  hasLossCascade: boolean;
  scopeLabel: string;
  sites: any[];
  fleet?: unknown;
  mode?: string;
  onPeriod?: (p: string) => void;
  onWindow?: (from: string, to: string) => void;
  window?: { start?: string; end?: string; start_at?: string; end_at?: string };
  navigate: any;
  tenant: string;
  quiet: any;
}

/* ════════════════════════════════════════════════════════════════════════
   Executive composition (wireframe 02)
   ════════════════════════════════════════════════════════════════════════ */

function HomeExecutive({ h, period, hasValueLens, hasLossCascade, scopeLabel, sites }: CompositionProps): React.ReactNode {
  const promise = h.promise;
  const losses: LossCascadeRow[] = h.losses ?? [];
  const docket = h.docket ?? [];
  const autonomy = h.autonomy;

  const lossSteps: CascadeStep[] = losses.map((r) => ({
    label: r.label ?? "—",
    value: typeof r.figure?.value === "number" ? r.figure.value : null,
    status: mapStatus(r.row_status),
  }));

  return (
    <div className="sk-home">
      <PageHeader
        title="Home"
        scope={`${scopeLabel}${sites.length ? ` · ${sites.length} sites` : ""}${period ? ` · ${period}` : ""}`}
      />

      {/* Row 1: promise (1.3fr) + platform did (1fr) */}
      <div style={ROW_13_1}>
        <div style={CARD}>
          <div style={CARD_HEAD}>
            <span style={CARD_TITLE}>Delivered against promise</span>
            <span style={CARD_SUB}>month to date</span>
          </div>
          <div style={ROW_3COL}>
            <div>
              <div style={MET_LABEL}>Energy delivered</div>
              <div style={MET_VAL_BIG}>
                {typeof promise?.energy_delivered?.value === "number"
                  ? <ScaledVal figure={promise.energy_delivered} />
                  : withheldFig(promise?.energy_delivered)}
              </div>
              <div style={MET_SUB}>
                {promise?.energy_delivered?.basis ? (
                  <span style={CHIP_BASIS}>{promise.energy_delivered.basis}</span>
                ) : null}
                {promise?.plan && typeof promise.plan.value === "number"
                  ? ` of ${scaledText(promise.plan)} planned`
                  : null}
                {promise?.attainment_ratio && typeof promise.attainment_ratio.value === "number"
                  ? ` · ${formatNum(promise.attainment_ratio.value)}%`
                  : null}
              </div>
            </div>
            {hasValueLens ? (
              <div>
                <div style={MET_LABEL}>Value delivered</div>
                <div style={MET_VAL_BIG}>
                  {promise?.value_delivered
                    ? <Money money={promise.value_delivered} />
                    : withheld("CANNOT_CHECK_NO_VALUE_FIGURE")}
                </div>
                <div style={MET_SUB}>
                  {promise?.value_delivered?.currency ? (
                    <span style={CHIP}>revenue · PPA tariff</span>
                  ) : null}
                </div>
              </div>
            ) : null}
            <div>
              <div style={MET_LABEL}>Contracted availability</div>
              {h.contract_availability && typeof h.contract_availability.value === "number" ? (
                <div style={MET_VAL_BIG}>
                  {formatNum(h.contract_availability.value)}
                  <small style={MET_UNIT}>%</small>
                </div>
              ) : (
                // Was a hardcoded "Cannot check" in the health-warning colour, which
                // broke two rules at once: colour carries equipment health and
                // nothing else (UX-003 §3.7), and the state word was a literal, so
                // it said CANNOT_CHECK whatever the server actually reported. The
                // server sends its own state and a true reason — use them.
                <div style={MET_VAL}>{withheldFig(h.contract_availability)}</div>
              )}
            </div>
          </div>

          {promise?.attainment_ratio && typeof promise.attainment_ratio.value === "number" ? (
            <div style={{ marginTop: 12 }}>
              <div style={BAR_TRACK}>
                <div style={{ ...BAR_FILL, width: `${Math.min(promise.attainment_ratio.value, 100)}%` }} />
              </div>
              <div style={{ ...MET_SUB, marginTop: 4 }}>
                Attainment by contract, excusable events separated
              </div>
            </div>
          ) : null}
        </div>

        <div style={CARD}>
          <div style={CARD_HEAD}>
            <span style={CARD_TITLE}>What the platform did</span>
            <span style={CARD_SUB}>this month</span>
          </div>
          <div style={ROW_2COL}>
            <div>
              <div style={MET_LABEL}>Actions within policy</div>
              {/* The autonomy block is a Phase-5 seam: the backend returns every
                  figure NOT_APPLICABLE with a reason, so render the state word it
                  sent instead of a dash that reads like a missing number. */}
              <div style={MET_VAL}>
                {typeof autonomy?.actions_within_policy?.value === "number"
                  ? autonomy.actions_within_policy.value
                  : withheldFig(autonomy?.actions_within_policy)}
              </div>
              <div style={MET_SUB}>
                {autonomy?.verified && typeof autonomy.verified.value === "number" ? (
                  <><span style={{ color: "var(--sk-color-health-ok)" }}>● </span>{autonomy.verified.value} verified</>
                ) : null}
                {autonomy?.unverified && typeof autonomy.unverified.value === "number"
                  ? ` · ${autonomy.unverified.value} unverified` : null}
                {autonomy?.reverted && typeof autonomy.reverted.value === "number"
                  ? ` · ${autonomy.reverted.value} reverted` : null}
              </div>
            </div>
            <div>
              <div style={MET_LABEL}>Energy recovered</div>
              <div style={MET_VAL}>
                {typeof autonomy?.energy_recovered?.value === "number"
                  ? <ScaledVal figure={autonomy.energy_recovered} />
                  : withheldFig(autonomy?.energy_recovered)}
              </div>
              <div style={MET_SUB}>measured against expected after closure</div>
            </div>
            <div>
              <div style={MET_LABEL}>Unmanned hours</div>
              <div style={MET_VAL}>
                {typeof autonomy?.unmanned_hours?.value === "number"
                  ? <>{formatNum(autonomy.unmanned_hours.value)}<small style={MET_UNIT}>%</small></>
                  : withheldFig(autonomy?.unmanned_hours)}
              </div>
              <div style={MET_SUB}>of daylight hours, {sites.length} sites</div>
            </div>
            <div>
              <div style={MET_LABEL}>Awaiting a human</div>
              <div style={MET_VAL}>
                {typeof autonomy?.awaiting_human?.value === "number"
                  ? autonomy.awaiting_human.value
                  : withheldFig(autonomy?.awaiting_human)}
              </div>
              <div style={MET_SUB}>
                {docket.length > 0 ? `${docket.length} on the docket` : "nothing pending"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: loss cascade (1.3fr) + docket (1fr) */}
      <div style={ROW_13_1}>
        {hasLossCascade ? (
          <div style={CARD}>
            <div style={CARD_HEAD}>
              <span style={CARD_TITLE}>Where the value went</span>
              <span style={CARD_SUB}>loss cascade, month to date{hasValueLens ? ", currency under Value lens" : ""}</span>
            </div>
            {lossSteps.length > 0 ? (
              <LossCascade
                unit={losses.find((r) => r.figure?.unit)?.figure?.unit ?? ""}
                steps={lossSteps}
                renderValue={(s) => renderLossValue(losses, s, hasValueLens)}
              />
            ) : (
              <EmptyState variant="verified_quiet" title="No losses to attribute" />
            )}
          </div>
        ) : (
          <div style={CARD}>
            <div style={CARD_HEAD}>
              <span style={CARD_TITLE}>Where the value went</span>
            </div>
            <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
          </div>
        )}

        <div style={CARD}>
          <div style={CARD_HEAD}>
            <span style={CARD_TITLE}>Needs you</span>
            <span style={CARD_SUB}>the docket</span>
            <span style={CARD_RIGHT}>{docket.length} items</span>
          </div>
          {docket.length > 0 ? (
            <div>
              {docket.map((item: any, i: number) => (
                <DocketRow key={`${item.kind ?? "d"}-${i}`} item={item} hasValueLens={hasValueLens} />
              ))}
            </div>
          ) : (
            <EmptyState variant="verified_quiet" title="All clear" message="Nothing on the docket." />
          )}
        </div>
      </div>

      {/* Row 3: Risks — Contracts lens */}
      <div style={CARD}>
        <div style={CARD_HEAD}>
          <span style={CARD_TITLE}>Risks</span>
          <span style={CARD_SUB}>Contracts lens</span>
        </div>
        <div style={ROW_4COL}>
          <div>
            <div style={MET_LABEL}>Covenant watch</div>
            {h.risks?.covenant_watch && typeof h.risks.covenant_watch.value === "number" ? (
              <div style={MET_VAL}>{h.risks.covenant_watch.value}<small style={MET_UNIT}>SPVs</small></div>
            ) : (
              <StateWord state="NOT_APPLICABLE" reason={{ template: "NOT_APPLICABLE_NO_CONTRACTS" }} />
            )}
          </div>
          <div>
            <div style={MET_LABEL}>PPA compliance</div>
            {h.risks?.ppa_compliance && typeof h.risks.ppa_compliance.value === "number" ? (
              <div style={MET_VAL}>{h.risks.ppa_compliance.value}<small style={MET_UNIT}>%</small></div>
            ) : (
              <StateWord state="NOT_APPLICABLE" reason={{ template: "NOT_APPLICABLE_NO_CONTRACTS" }} />
            )}
          </div>
          <div>
            <div style={MET_LABEL}>Expiring in 12 months</div>
            {h.risks?.expiries?.length ? (
              <div style={MET_VAL}>{h.risks.expiries.length}<small style={MET_UNIT}>contracts</small></div>
            ) : (
              <StateWord state="NOT_APPLICABLE" reason={{ template: "NOT_APPLICABLE_NO_CONTRACTS" }} />
            )}
          </div>
          <div>
            <div style={MET_LABEL}>Claims open</div>
            {h.risks?.claims?.length ? (
              <div style={MET_VAL}>{h.risks.claims.length}</div>
            ) : (
              <StateWord state="NOT_APPLICABLE" reason={{ template: "NOT_APPLICABLE_NO_CONTRACTS" }} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Regional Operations composition (wireframe 03)
   ════════════════════════════════════════════════════════════════════════ */

function HomeRegionalOps({ h, period, hasValueLens, scopeLabel, sites, navigate, tenant, quiet }: CompositionProps): React.ReactNode {
  const docket = h.docket ?? [];
  // A missing liveness read is WITHHELD, never "every site is up" (which is what
  // falling back to the site count used to assert).
  const upCount = sitesUpOf(h);
  const downCount = h.data_path?.sites_down?.length ?? 0;

  const ranked = [...sites].sort(
    (a, b) => figVal(b.energy_at_stake) - figVal(a.energy_at_stake)
      || figVal(b.energy_today) - figVal(a.energy_today),
  );
  const top = ranked.slice(0, 6);
  const rest = ranked.length - top.length;

  // energy_at_stake is WITHHELD on every row until the value-at-stake engine lands,
  // so there is no fleet total to show. Summing figVal()'s -1 sentinel produced a
  // nonsense number; the tile now states that it cannot be checked.
  const atStakeKnown = sites.filter((s) => typeof s.energy_at_stake?.value === "number");
  const totalAtStake = atStakeKnown.length > 0
    ? atStakeKnown.reduce((sum, s) => sum + (s.energy_at_stake.value as number), 0)
    : null;

  const openSite = (slug: string) =>
    void navigate({
      to: "/t/$tenant/s/$scope/plants/$site/now",
      params: { tenant, scope: tenant, site: slug },
      search: { period: "today", mode: "live" },
    });

  return (
    <div className="sk-home">
      <PageHeader
        title="Home"
        scope={`${scopeLabel} · ${sites.length} sites${period ? ` · ${period}` : ""}`}
      />

      {/* KPI strip — 5 tiles (wireframe 03 annotation 1, 2) */}
      <div style={ROW_5COL}>
        <div style={CARD}>
          <div style={MET_LABEL}>Energy at stake now</div>
          <div style={MET_VAL}>
            {totalAtStake === null
              ? withheld("CANNOT_CHECK_NO_VALUE_AT_STAKE_ENGINE")
              : <>{formatNum(totalAtStake)}<small style={MET_UNIT}>MWh/d</small></>}
          </div>
          <div style={MET_SUB}><span style={CHIP}>ranked below</span></div>
        </div>
        <div style={CARD}>
          <div style={MET_LABEL}>Queue</div>
          <div style={MET_VAL}>
            {docket.length}<small style={MET_UNIT}>open</small>
          </div>
          <div style={MET_SUB}>
            {docket.filter((d: any) => d.primary_action === "approve").length} awaiting you
          </div>
        </div>
        <div style={CARD}>
          <div style={MET_LABEL}>Data path</div>
          <div style={MET_VAL}>
            {upCount === null
              ? withheld("CANNOT_CHECK_NO_LIVE_PATH")
              : <>{upCount} / {upCount + downCount}<small style={MET_UNIT}>up</small></>}
          </div>
          <div style={MET_SUB}>
            {upCount === null ? null : downCount > 0 ? (
              <span style={{ color: "var(--sk-color-health-offline)" }}>{downCount} down</span>
            ) : "all healthy"}
          </div>
        </div>
        <div style={CARD}>
          <div style={MET_LABEL}>Coverage</div>
          {h.data_path?.coverage && typeof h.data_path.coverage.value === "number" ? (
            <>
              <div style={MET_VAL}>{formatNum(h.data_path.coverage.value)}<small style={MET_UNIT}>%</small></div>
              <div style={MET_SUB}>signal level</div>
            </>
          ) : (
            <div style={MET_VAL}>{withheldFig(h.data_path?.coverage, "CANNOT_CHECK_NO_LIVE_PATH")}</div>
          )}
        </div>
        <div style={CARD}>
          <div style={MET_LABEL}>Crew today</div>
          <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
        </div>
      </div>

      {/* Main body: sites table (1.4fr) + approvals + crew/SLA (1fr) */}
      <div style={ROW_14_1}>
        {/* Sites ranked by value at stake (wireframe 03 annotation 3) */}
        <div style={CARD}>
          <div style={CARD_HEAD}>
            <span style={CARD_TITLE}>Sites ranked by value at stake</span>
            <span style={CARD_SUB}>today · MWh{hasValueLens ? " then currency where a tariff exists" : ""}</span>
            <span style={CARD_RIGHT}>
              <button
                type="button"
                style={BTN_SMALL}
                onClick={() => void navigate({ to: "/t/$tenant/s/$scope/plants", params: { tenant, scope: tenant }, search: { period: "today" } })}
              >
                Open fleet grid
              </button>
            </span>
          </div>
          {top.length > 0 ? (
            <table style={TABLE}>
              <thead>
                <tr>
                  <th style={TH}>Site</th>
                  <th style={TH}>Health</th>
                  <th style={{ ...TH, textAlign: "right" }}>At stake</th>
                  <th style={TH}>PR</th>
                  <th style={{ ...TH, textAlign: "right" }}>Open</th>
                </tr>
              </thead>
              <tbody>
                {top.map((s, i) => (
                  <tr
                    key={s.site_id ?? i}
                    style={{ ...TR, cursor: "pointer" }}
                    onClick={() => s.site_id && openSite(s.site_id)}
                  >
                    <td style={TD}>
                      <span style={{ fontWeight: 600 }}>{s.site_id}</span>
                    </td>
                    <td style={TD}>
                      <HealthBadge
                        state={(s.health?.state as HealthState) ?? "unmonitored"}
                        modifiers={s.health?.modifiers as HealthModifier[] | undefined}
                        showLabel
                      />
                    </td>
                    <td style={{ ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {inlineFig(s.energy_at_stake)}
                    </td>
                    <td style={TD}>{inlineFig(s.pr)}</td>
                    <td style={{ ...TD, textAlign: "right" }}>
                      {typeof s.open_interventions === "number"
                        ? s.open_interventions
                        : withheld("CANNOT_CHECK_NO_INTERVENTION_COUNT")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyState variant="no_data" title="No sites in scope" />
          )}
          {rest > 0 ? (
            <div style={{ ...MET_SUB, padding: "8px 0 2px" }}>
              {rest} {rest === 1 ? "site" : "sites"} within normal range · verified quiet · data path healthy
            </div>
          ) : null}
        </div>

        {/* Right column: approvals + crew/SLA */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Awaiting your approval (wireframe 03 annotation 5) */}
          <div style={CARD}>
            <div style={CARD_HEAD}>
              <span style={CARD_TITLE}>Awaiting your approval</span>
              <span style={CARD_SUB}>authority level</span>
            </div>
            {docket.length > 0 ? (
              <div>
                {docket.map((item: any, i: number) => (
                  <DocketRowWithAction key={`${item.kind ?? "d"}-${i}`} item={item} />
                ))}
              </div>
            ) : quiet && "is_quiet" in quiet && quiet.is_quiet ? (
              <EmptyState variant="verified_quiet" title="All clear" message={"text" in quiet ? (quiet.text ?? undefined) : undefined} />
            ) : (
              <EmptyState variant="no_data" title="Nothing awaiting approval" />
            )}
          </div>

          {/* Crew and SLA (wireframe 03 annotation 6) — P2 */}
          <div style={CARD}>
            <div style={CARD_HEAD}>
              <span style={CARD_TITLE}>Crew and SLA</span>
              <span style={CARD_SUB}>today</span>
            </div>
            <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
          </div>

          {/* PM calendar (UX-002 SS4) — P2 */}
          <section style={CARD}>
            <h2 style={H2_MUTED}>PM calendar</h2>
            <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
          </section>

          {/* Benchmarking (UX-002 SS4) — P2 */}
          <section style={CARD}>
            <h2 style={H2_MUTED}>Benchmarking</h2>
            <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
          </section>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Asset Manager composition (AM)
   ════════════════════════════════════════════════════════════════════════ */

function HomeAssetManager({ h, period, hasValueLens, scopeLabel, sites }: CompositionProps): React.ReactNode {
  const promise = h.promise;

  return (
    <div className="sk-home">
      <PageHeader
        title="Home"
        scope={`${scopeLabel}${sites.length ? ` · ${sites.length} sites` : ""}${period ? ` · ${period}` : ""}`}
      />

      {/* KPI strip */}
      <div style={ROW_4COL}>
        <div style={CARD}>
          <div style={MET_LABEL}>Energy delivered</div>
          {promise?.energy_delivered ? (
            <>
              <div style={MET_VAL}>
                {typeof promise.energy_delivered.value === "number"
                  ? <ScaledVal figure={promise.energy_delivered} />
                  : withheldFig(promise.energy_delivered)}
              </div>
              <div style={MET_SUB}>
                {promise?.energy_delivered?.basis ? (
                  <span style={CHIP_BASIS}>{promise.energy_delivered.basis}</span>
                ) : null}
              </div>
            </>
          ) : (
            <div style={MET_VAL}>{withheldFig(promise?.energy_delivered)}</div>
          )}
        </div>
        {hasValueLens ? (
          <div style={CARD}>
            <div style={MET_LABEL}>Value delivered</div>
            <div style={MET_VAL_BIG}>
              {promise?.value_delivered
                ? <Money money={promise.value_delivered} />
                : withheld("CANNOT_CHECK_NO_VALUE_FIGURE")}
            </div>
          </div>
        ) : null}
        <div style={CARD}>
          <div style={MET_LABEL}>Contract availability</div>
          {h.contract_availability && typeof h.contract_availability.value === "number" ? (
            <div style={MET_VAL}>{formatNum(h.contract_availability.value)}<small style={MET_UNIT}>%</small></div>
          ) : (
            // The server states WHY (CANNOT_CHECK_AVAILABILITY_ENGINE); a literal
            // placeholder here threw that away and answered in its own words.
            withheldFig(h.contract_availability, "NOT_MEASURED_PENDING_ENGINE")
          )}
        </div>
        <div style={CARD}>
          <div style={MET_LABEL}>Sites in scope</div>
          <div style={MET_VAL}>{sites.length}</div>
          <div style={MET_SUB}>{scopeLabel}</div>
        </div>
      </div>

      {/* Main body */}
      <div style={ROW_13_1}>
        {/* Left: attainment + contractor scorecards */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <section style={CARD}>
            <h2 style={H2_MUTED}>Attainment by contract</h2>
            <div style={MET_SUB}>excusable events separated</div>
            {promise?.energy_delivered ? (
              <Metric figure={promise.energy_delivered} showProvenance />
            ) : null}
            {typeof promise?.attainment_ratio?.value === "number" ? (
              <AttainmentBar
                valuePct={promise.attainment_ratio.value}
                planMarkerPct={100}
                status={
                  promise.attainment_ratio.value >= 100
                    ? "ahead"
                    : promise.attainment_ratio.value >= 90
                      ? "on_plan"
                      : "behind"
                }
                label="Attainment"
              />
            ) : promise?.plan ? (
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                <span style={MET_LABEL}>Plan</span>
                <Metric figure={promise.plan} />
              </div>
            ) : null}
            {hasValueLens ? <Money money={promise?.value_delivered} /> : null}
          </section>

          <section style={CARD}>
            <h2 style={H2_MUTED}>Contractor scorecards</h2>
            <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
          </section>
        </div>

        {/* Right: claims + monthly pack */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <section style={CARD}>
            <h2 style={H2_MUTED}>Claims and guarantee windows</h2>
            <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
          </section>
          <section style={CARD}>
            <h2 style={H2_MUTED}>Monthly pack status</h2>
            <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
          </section>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Control Room composition (CR)
   ════════════════════════════════════════════════════════════════════════ */

function HomeControlRoom({ h, period, scopeLabel, sites, quiet }: CompositionProps): React.ReactNode {
  const docket = h.docket ?? [];
  // WITHHELD when the liveness read produced no count — never "all sites up".
  const upCount = sitesUpOf(h);
  const downCount = h.data_path?.sites_down?.length ?? 0;

  return (
    <div className="sk-home">
      <PageHeader
        title="Home"
        scope={`${scopeLabel} · ${sites.length} sites${period ? ` · ${period}` : ""}`}
      />

      {/* Data path health — prominent, full width (spec: "data-path health first") */}
      <div style={CARD}>
        <div style={CARD_HEAD}>
          <span style={CARD_TITLE}>Data path health</span>
          <span style={CARD_SUB}>shift view</span>
        </div>
        <div style={ROW_3COL}>
          <div>
            <div style={MET_LABEL}>Sites up</div>
            <div style={MET_VAL_BIG}>
              {upCount === null ? withheld("CANNOT_CHECK_NO_LIVE_PATH") : upCount}
            </div>
            <div style={MET_SUB}>{upCount === null ? null : `of ${upCount + downCount}`}</div>
          </div>
          <div>
            <div style={MET_LABEL}>Sites down</div>
            <div style={{ ...MET_VAL_BIG, color: downCount > 0 ? "var(--sk-color-health-fault)" : "var(--sk-color-ink-1)" }}>
              {downCount}
            </div>
            {downCount > 0 && h.data_path?.sites_down ? (
              <div style={MET_SUB}>
                {h.data_path.sites_down.slice(0, 3).map((s: any) =>
                  typeof s === "string" ? s : s.site_id ?? "—",
                ).join(", ")}
                {h.data_path.sites_down.length > 3 ? ` + ${h.data_path.sites_down.length - 3} more` : ""}
              </div>
            ) : null}
          </div>
          <div>
            <div style={MET_LABEL}>Coverage</div>
            {h.data_path?.coverage && typeof h.data_path.coverage.value === "number" ? (
              <>
                <div style={MET_VAL_BIG}>{formatNum(h.data_path.coverage.value)}<small style={MET_UNIT}>%</small></div>
                <div style={MET_SUB}>signal level</div>
              </>
            ) : (
              <div style={MET_VAL}>{withheldFig(h.data_path?.coverage, "CANNOT_CHECK_NO_LIVE_PATH")}</div>
            )}
          </div>
        </div>
        {h.data_path?.coverage && typeof h.data_path.coverage.value === "number" ? (
          <CoverageBar pct={h.data_path.coverage.value} />
        ) : null}
      </div>

      {/* Main body */}
      <div style={ROW_13_1}>
        {/* Left: awaiting human + new detections */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={CARD}>
            <div style={CARD_HEAD}>
              <span style={CARD_TITLE}>Items awaiting a human</span>
              <span style={CARD_RIGHT}>{docket.length} items</span>
            </div>
            {docket.length > 0 ? (
              <div>
                {docket.map((item: any, i: number) => (
                  <DocketRowWithAction key={`${item.kind ?? "d"}-${i}`} item={item} />
                ))}
              </div>
            ) : quiet && "is_quiet" in quiet && quiet.is_quiet ? (
              <EmptyState variant="verified_quiet" title="Verified quiet" message={"text" in quiet ? (quiet.text ?? undefined) : undefined} />
            ) : (
              <EmptyState variant="no_data" title="Nothing awaiting action" />
            )}
          </div>

          <section style={CARD}>
            <h2 style={H2_MUTED}>New detections</h2>
            <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
          </section>
        </div>

        {/* Right: escalations + verified quiet */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <section style={CARD}>
            <h2 style={H2_MUTED}>Escalations</h2>
            <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
          </section>

          <div style={CARD}>
            <div style={CARD_HEAD}>
              <span style={CARD_TITLE}>Verified quiet</span>
            </div>
            {quiet && "is_quiet" in quiet && quiet.is_quiet ? (
              <EmptyState
                variant="verified_quiet"
                title="All systems verified quiet"
                message={"text" in quiet ? (quiet.text ?? undefined) : undefined}
              />
            ) : (
              <div style={MET_SUB}>
                {upCount === null
                  ? <>Sites reporting {withheld("CANNOT_CHECK_NO_LIVE_PATH")}</>
                  : `${upCount} of ${upCount + downCount} sites reporting`}
                {" · "}
                {docket.length === 0 ? "nothing on the docket" : `${docket.length} items pending`}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Site composition (SM, SE)
   ════════════════════════════════════════════════════════════════════════ */

function HomeSite({ h, period, hasValueLens, scopeLabel, quiet }: CompositionProps): React.ReactNode {
  const promise = h.promise;
  const docket = h.docket ?? [];
  // WITHHELD when the liveness read produced no count — never "all sites up".
  const upCount = sitesUpOf(h);
  const downCount = h.data_path?.sites_down?.length ?? 0;

  return (
    <div className="sk-home">
      <PageHeader
        title="Home"
        scope={`${scopeLabel}${period ? ` · ${period}` : ""}`}
      />

      {/* KPI strip — 5 tiles matching site-level metrics */}
      <div style={ROW_5COL}>
        <div style={CARD}>
          <div style={MET_LABEL}>Energy today</div>
          {promise?.energy_delivered ? (
            <>
              <div style={MET_VAL}>
                {typeof promise.energy_delivered.value === "number"
                  ? <>{formatNum(promise.energy_delivered.value)}<small style={MET_UNIT}>{promise.energy_delivered.unit ?? "MWh"}</small></>
                  : withheldFig(promise.energy_delivered)}
              </div>
              <div style={MET_SUB}>
                {promise?.energy_delivered?.basis ? (
                  <span style={CHIP_BASIS}>{promise.energy_delivered.basis}</span>
                ) : null}
              </div>
            </>
          ) : (
            <div style={MET_VAL}>{withheldFig(promise?.energy_delivered)}</div>
          )}
        </div>
        <div style={CARD}>
          <div style={MET_LABEL}>Availability</div>
          {h.contract_availability && typeof h.contract_availability.value === "number" ? (
            <div style={MET_VAL}>{formatNum(h.contract_availability.value)}<small style={MET_UNIT}>%</small></div>
          ) : (
            // The server states WHY (CANNOT_CHECK_AVAILABILITY_ENGINE); a literal
            // placeholder here threw that away and answered in its own words.
            withheldFig(h.contract_availability, "NOT_MEASURED_PENDING_ENGINE")
          )}
        </div>
        <div style={CARD}>
          <div style={MET_LABEL}>Coverage</div>
          {h.data_path?.coverage && typeof h.data_path.coverage.value === "number" ? (
            <>
              <div style={MET_VAL}>{formatNum(h.data_path.coverage.value)}<small style={MET_UNIT}>%</small></div>
              <div style={MET_SUB}>signal level</div>
            </>
          ) : (
            <div style={MET_VAL}>{withheldFig(h.data_path?.coverage, "CANNOT_CHECK_NO_LIVE_PATH")}</div>
          )}
        </div>
        <div style={CARD}>
          <div style={MET_LABEL}>Data path</div>
          <div style={MET_VAL}>
            {upCount === null
              ? withheld("CANNOT_CHECK_NO_LIVE_PATH")
              : <>{upCount}<small style={MET_UNIT}>up</small></>}
          </div>
          <div style={MET_SUB}>
            {upCount === null ? null : downCount > 0 ? `${downCount} down` : "healthy"}
          </div>
        </div>
        <div style={CARD}>
          <div style={MET_LABEL}>Open actions</div>
          <div style={MET_VAL}>{docket.length}</div>
          <div style={MET_SUB}>on the docket</div>
        </div>
      </div>

      {/* Main body */}
      <div style={ROW_14_1}>
        {/* Left: open actions + blocks */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={CARD}>
            <div style={CARD_HEAD}>
              <span style={CARD_TITLE}>Open actions</span>
              <span style={CARD_SUB}>ranked by energy at stake</span>
              <span style={CARD_RIGHT}>{docket.length} items</span>
            </div>
            {docket.length > 0 ? (
              <div>
                {docket.map((item: any, i: number) => (
                  <DocketRowWithAction key={`${item.kind ?? "d"}-${i}`} item={item} />
                ))}
              </div>
            ) : quiet && "is_quiet" in quiet && quiet.is_quiet ? (
              <EmptyState variant="verified_quiet" title="All clear" message={"text" in quiet ? (quiet.text ?? undefined) : undefined} />
            ) : (
              <EmptyState variant="no_data" title="No open actions" />
            )}
          </div>

          <section style={CARD}>
            <h2 style={H2_MUTED}>Blocks ranked by deviation</h2>
            <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
          </section>
        </div>

        {/* Right: coverage + day plan + permits */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {h.data_path?.coverage && typeof h.data_path.coverage.value === "number" ? (
            <div style={CARD}>
              <div style={CARD_HEAD}>
                <span style={CARD_TITLE}>Coverage</span>
              </div>
              <CoverageBar pct={h.data_path.coverage.value} />
            </div>
          ) : null}

          {hasValueLens && promise?.value_delivered ? (
            <div style={CARD}>
              <div style={CARD_HEAD}>
                <span style={CARD_TITLE}>Value</span>
              </div>
              <Money money={promise.value_delivered} />
            </div>
          ) : null}

          <section style={CARD}>
            <h2 style={H2_MUTED}>Day plan</h2>
            <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
          </section>
          <section style={CARD}>
            <h2 style={H2_MUTED}>Permits and safety</h2>
            <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
          </section>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Technician composition (TE) — field application
   ════════════════════════════════════════════════════════════════════════ */

function HomeTechnician({ h, period, scopeLabel, quiet }: CompositionProps): React.ReactNode {
  const docket = h.docket ?? [];

  return (
    <div className="sk-home">
      <PageHeader
        title="Home"
        scope={`${scopeLabel}${period ? ` · ${period}` : ""}`}
      />

      {/* My tasks — full width, primary panel */}
      <div style={CARD}>
        <div style={CARD_HEAD}>
          <span style={CARD_TITLE}>My tasks</span>
          <span style={CARD_SUB}>in order of priority</span>
          <span style={CARD_RIGHT}>{docket.length} assigned</span>
        </div>
        {docket.length > 0 ? (
          <div>
            {docket.map((item: any, i: number) => (
              <div key={`${item.kind ?? "t"}-${i}`} style={{ ...DOCKET_IT, gridTemplateColumns: "auto 1fr auto" }}>
                <span style={{ ...CHIP, background: i === 0 ? "var(--sk-color-accent-soft)" : undefined, color: i === 0 ? "var(--sk-color-accent)" : undefined }}>
                  {i + 1}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 12 }}>{item.what}</div>
                </div>
                <button type="button" style={i === 0 ? BTN_PRIMARY : BTN_SMALL}>
                  {i === 0 ? "Start" : "View"}
                </button>
              </div>
            ))}
          </div>
        ) : quiet && "is_quiet" in quiet && quiet.is_quiet ? (
          <EmptyState variant="verified_quiet" title="No tasks assigned" message={"text" in quiet ? (quiet.text ?? undefined) : undefined} />
        ) : (
          <EmptyState variant="no_data" title="No tasks assigned" />
        )}
      </div>

      {/* Secondary panels */}
      <div style={ROW_2COL}>
        <section style={CARD}>
          <h2 style={H2_MUTED}>Safety checklist</h2>
          <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
        </section>
        {/* There is no offline/sync subsystem, so there is no sync state to report.
            The old static "Online · last sync just now" asserted one unconditionally. */}
        <section style={CARD}>
          <h2 style={H2_MUTED}>Sync state</h2>
          <StateWord state="NOT_APPLICABLE" reason={{ template: "NOT_APPLICABLE_NO_OFFLINE_SYNC" }} />
        </section>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Contractor composition (CM)
   ════════════════════════════════════════════════════════════════════════ */

function HomeContractor({ h, period, scopeLabel, sites, navigate, tenant, quiet }: CompositionProps): React.ReactNode {
  const docket = h.docket ?? [];

  const ranked = [...sites].sort(
    (a, b) => (typeof b.open_interventions === "number" ? b.open_interventions : 0)
      - (typeof a.open_interventions === "number" ? a.open_interventions : 0),
  );
  const top = ranked.slice(0, 6);

  const openSite = (slug: string) =>
    void navigate({
      to: "/t/$tenant/s/$scope/plants/$site/now",
      params: { tenant, scope: tenant, site: slug },
      search: { period: "today", mode: "live" },
    });

  return (
    <div className="sk-home">
      <PageHeader
        title="Home"
        scope={`${scopeLabel}${sites.length ? ` · ${sites.length} sites` : ""}${period ? ` · ${period}` : ""}`}
      />

      {/* KPI strip */}
      <div style={ROW_4COL}>
        <div style={CARD}>
          <div style={MET_LABEL}>Queue</div>
          <div style={MET_VAL}>{docket.length}<small style={MET_UNIT}>open</small></div>
          <div style={MET_SUB}>across contracts</div>
        </div>
        <div style={CARD}>
          <div style={MET_LABEL}>Sites assigned</div>
          <div style={MET_VAL}>{sites.length}</div>
        </div>
        <div style={CARD}>
          <div style={MET_LABEL}>SLA status</div>
          <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
        </div>
        <div style={CARD}>
          <div style={MET_LABEL}>Crew on shift</div>
          <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
        </div>
      </div>

      {/* Main body */}
      <div style={ROW_13_1}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Queue */}
          <div style={CARD}>
            <div style={CARD_HEAD}>
              <span style={CARD_TITLE}>Assigned actions</span>
              <span style={CARD_SUB}>queue and SLA across contracts</span>
              <span style={CARD_RIGHT}>{docket.length} items</span>
            </div>
            {docket.length > 0 ? (
              <div>
                {docket.map((item: any, i: number) => (
                  <DocketRowWithAction key={`${item.kind ?? "c"}-${i}`} item={item} />
                ))}
              </div>
            ) : quiet && "is_quiet" in quiet && quiet.is_quiet ? (
              <EmptyState variant="verified_quiet" title="All clear" message={"text" in quiet ? (quiet.text ?? undefined) : undefined} />
            ) : (
              <EmptyState variant="no_data" title="No assigned actions" />
            )}
          </div>

          {/* Sites with open items */}
          {top.length > 0 ? (
            <div style={CARD}>
              <div style={CARD_HEAD}>
                <span style={CARD_TITLE}>Sites with open items</span>
              </div>
              {top.map((s, i) => (
                <button
                  type="button"
                  key={s.site_id ?? i}
                  className="sk-siterow"
                  onClick={() => s.site_id && openSite(s.site_id)}
                >
                  <HealthBadge
                    state={(s.health?.state as HealthState) ?? "unmonitored"}
                    modifiers={s.health?.modifiers as HealthModifier[] | undefined}
                    showLabel={false}
                  />
                  <span>
                    <span className="sk-siterow__name">{s.site_id}</span>
                    <span className="sk-siterow__meta">
                      {typeof s.open_interventions === "number" && s.open_interventions > 0
                        ? ` · ${s.open_interventions} open` : ""}
                    </span>
                  </span>
                  <span className="sk-siterow__fig">{inlineFig(bestFigure(s))}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <section style={CARD}>
            <h2 style={H2_MUTED}>Crew allocation</h2>
            <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
          </section>
          <section style={CARD}>
            <h2 style={H2_MUTED}>Spares</h2>
            <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
          </section>
          <section style={CARD}>
            <h2 style={H2_MUTED}>Client report status</h2>
            <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
          </section>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Viewer composition (V) — read-only
   ════════════════════════════════════════════════════════════════════════ */

function HomeViewer({ h, period, hasValueLens: _vl, scopeLabel, sites, navigate, tenant }: CompositionProps): React.ReactNode {
  const promise = h.promise;
  // WITHHELD when the liveness read produced no count — never "all sites up".
  const upCount = sitesUpOf(h);
  const downCount = h.data_path?.sites_down?.length ?? 0;

  const openSite = (slug: string) =>
    void navigate({
      to: "/t/$tenant/s/$scope/plants/$site/now",
      params: { tenant, scope: tenant, site: slug },
      search: { period: "today", mode: "live" },
    });

  return (
    <div className="sk-home">
      <PageHeader
        title="Home"
        scope={`${scopeLabel}${sites.length ? ` · ${sites.length} sites` : ""}${period ? ` · ${period}` : ""}`}
      />

      {/* KPI strip */}
      <div style={ROW_4COL}>
        <div style={CARD}>
          <div style={MET_LABEL}>Energy delivered</div>
          {promise?.energy_delivered ? (
            <div style={MET_VAL}>
              {typeof promise.energy_delivered.value === "number"
                ? <ScaledVal figure={promise.energy_delivered} />
                : withheldFig(promise.energy_delivered)}
            </div>
          ) : (
            <div style={MET_VAL}>{withheldFig(promise?.energy_delivered)}</div>
          )}
        </div>
        <div style={CARD}>
          <div style={MET_LABEL}>Data path</div>
          <div style={MET_VAL}>
            {upCount === null
              ? withheld("CANNOT_CHECK_NO_LIVE_PATH")
              : <>{upCount}<small style={MET_UNIT}>up</small></>}
          </div>
          <div style={MET_SUB}>
            {upCount === null ? null : downCount > 0 ? `${downCount} down` : "all healthy"}
          </div>
        </div>
        {h.data_path?.coverage && typeof h.data_path.coverage.value === "number" ? (
          <div style={CARD}>
            <div style={MET_LABEL}>Coverage</div>
            <div style={MET_VAL}>{formatNum(h.data_path.coverage.value)}<small style={MET_UNIT}>%</small></div>
          </div>
        ) : null}
        <div style={CARD}>
          <div style={MET_LABEL}>Sites in scope</div>
          <div style={MET_VAL}>{sites.length}</div>
          <div style={MET_SUB}>{scopeLabel}</div>
        </div>
      </div>

      {/* Sites list — read-only */}
      <div style={CARD}>
        <div style={CARD_HEAD}>
          <span style={CARD_TITLE}>Sites in scope</span>
          <span style={CARD_SUB}>read-only view</span>
        </div>
        {sites.length > 0 ? (
          <div>
            {sites.map((s, i) => (
              <button
                type="button"
                key={s.site_id ?? i}
                className="sk-siterow"
                onClick={() => s.site_id && openSite(s.site_id)}
              >
                <HealthBadge
                  state={(s.health?.state as HealthState) ?? "unmonitored"}
                  modifiers={s.health?.modifiers as HealthModifier[] | undefined}
                  showLabel={false}
                />
                <span>
                  <span className="sk-siterow__name">{s.site_id}</span>
                  <span className="sk-siterow__meta">
                    {" · "}PR {inlineFig(s.pr)}
                  </span>
                </span>
                <span className="sk-siterow__fig">{inlineFig(bestFigure(s))}</span>
              </button>
            ))}
          </div>
        ) : (
          <EmptyState variant="no_data" title="No sites in scope" />
        )}
      </div>

      <section style={CARD}>
        <h2 style={H2_MUTED}>Packs and ledgers</h2>
        <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
      </section>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   SolarKey Operations composition (OPS) — internal build
   ════════════════════════════════════════════════════════════════════════ */

function HomeOps({ h, period, scopeLabel, sites, quiet }: CompositionProps): React.ReactNode {
  const autonomy = h.autonomy;
  const docket = h.docket ?? [];
  // WITHHELD when the liveness read produced no count — never "all sites up".
  const upCount = sitesUpOf(h);
  const downCount = h.data_path?.sites_down?.length ?? 0;

  return (
    <div className="sk-home">
      <PageHeader
        title="Home"
        scope={`SolarKey Operations${sites.length ? ` · ${sites.length} sites across tenants` : ""}${period ? ` · ${period}` : ""}`}
      />

      {/* Data path health — prominent (spec: autonomy health across service tenants) */}
      <div style={CARD}>
        <div style={CARD_HEAD}>
          <span style={CARD_TITLE}>Data path health</span>
          <span style={CARD_SUB}>across service tenants</span>
        </div>
        <div style={ROW_4COL}>
          <div>
            <div style={MET_LABEL}>Sites up</div>
            <div style={MET_VAL}>
              {upCount === null ? withheld("CANNOT_CHECK_NO_LIVE_PATH") : upCount}
            </div>
          </div>
          <div>
            <div style={MET_LABEL}>Sites down</div>
            <div style={{ ...MET_VAL, color: downCount > 0 ? "var(--sk-color-health-fault)" : "var(--sk-color-ink-1)" }}>
              {downCount}
            </div>
          </div>
          <div>
            <div style={MET_LABEL}>Coverage</div>
            {h.data_path?.coverage && typeof h.data_path.coverage.value === "number" ? (
              <div style={MET_VAL}>{formatNum(h.data_path.coverage.value)}<small style={MET_UNIT}>%</small></div>
            ) : (
              <div style={MET_VAL}>{withheldFig(h.data_path?.coverage, "CANNOT_CHECK_NO_LIVE_PATH")}</div>
            )}
          </div>
          <div>
            <div style={MET_LABEL}>Sites in scope</div>
            <div style={MET_VAL}>{sites.length}</div>
            <div style={MET_SUB}>{scopeLabel}</div>
          </div>
        </div>
      </div>

      {/* Main body */}
      <div style={ROW_13_1}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Autonomy health */}
          <div style={CARD}>
            <div style={CARD_HEAD}>
              <span style={CARD_TITLE}>Autonomy health</span>
              <span style={CARD_SUB}>platform actions this period</span>
            </div>
            <div style={ROW_2COL}>
              <div>
                <div style={MET_LABEL}>Actions within policy</div>
                <div style={MET_VAL}>
                  {typeof autonomy?.actions_within_policy?.value === "number"
                    ? autonomy.actions_within_policy.value
                    : withheldFig(autonomy?.actions_within_policy)}
                </div>
                <div style={MET_SUB}>
                  {autonomy?.verified && typeof autonomy.verified.value === "number" ? (
                    <><span style={{ color: "var(--sk-color-health-ok)" }}>● </span>{autonomy.verified.value} verified</>
                  ) : null}
                  {autonomy?.unverified && typeof autonomy.unverified.value === "number"
                    ? ` · ${autonomy.unverified.value} unverified` : null}
                  {autonomy?.reverted && typeof autonomy.reverted.value === "number"
                    ? ` · ${autonomy.reverted.value} reverted` : null}
                </div>
              </div>
              <div>
                <div style={MET_LABEL}>Energy recovered</div>
                <div style={MET_VAL}>
                  {typeof autonomy?.energy_recovered?.value === "number"
                    ? <ScaledVal figure={autonomy.energy_recovered} />
                    : withheldFig(autonomy?.energy_recovered)}
                </div>
              </div>
            </div>
          </div>

          {/* Awaiting human */}
          <div style={CARD}>
            <div style={CARD_HEAD}>
              <span style={CARD_TITLE}>Awaiting a human</span>
              <span style={CARD_RIGHT}>{docket.length} items</span>
            </div>
            {docket.length > 0 ? (
              <div>
                {docket.map((item: any, i: number) => (
                  <DocketRow key={`${item.kind ?? "o"}-${i}`} item={item} hasValueLens={false} />
                ))}
              </div>
            ) : quiet && "is_quiet" in quiet && quiet.is_quiet ? (
              <EmptyState variant="verified_quiet" title="All clear" />
            ) : (
              <EmptyState variant="no_data" title="Nothing awaiting action" />
            )}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <section style={CARD}>
            <h2 style={H2_MUTED}>Exceptions</h2>
            <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
          </section>
          <section style={CARD}>
            <h2 style={H2_MUTED}>Escalations</h2>
            <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
          </section>
          <section style={CARD}>
            <h2 style={H2_MUTED}>Onboarding status</h2>
            <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_PENDING_ENGINE" }} />
          </section>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Shared sub-components
   ════════════════════════════════════════════════════════════════════════ */

function DocketRow({ item, hasValueLens }: { item: any; hasValueLens: boolean }): React.ReactNode {
  return (
    <div style={DOCKET_IT}>
      <span style={DOCKET_WHEN}>{docketWhen(item.when)}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 12 }}>{item.what}</div>
      </div>
      <span style={DOCKET_AMT}>
        {hasValueLens && item.money ? <Money money={item.money} /> : null}
      </span>
    </div>
  );
}

function DocketRowWithAction({ item }: { item: any }): React.ReactNode {
  const label = item.primary_action ? actionLabel(item.primary_action) : "View";
  return (
    <div style={DOCKET_IT}>
      <span style={DOCKET_WHEN}>{docketWhen(item.when)}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 12 }}>{item.what}</div>
      </div>
      <button type="button" style={BTN_PRIMARY}>{label}</button>
    </div>
  );
}

function FleetAlarmsSummary(): React.ReactNode {
  const query = useGetV1Alarms();
  if (query.isLoading || !query.data || query.data.status !== 200) return null;

  const t = query.data.data.data.totals;
  // A null total means "not reported", not "none". Collapsing both to 0 and then
  // hiding the card on 0+0 made an unreported alarm feed render as a QUIET FLEET —
  // the card simply vanished, which is the most dangerous reading in the product
  // (QA, 25 Oct; the ruling: a withheld figure is never zero). Unknown now says so;
  // a measured 0+0 still collapses, because that genuinely is nothing to report.
  if (t.warn == null && t.critical == null) {
    return (
      <section className="sk-card">
        <h2 className="text-sm text-text-muted">Fleet alarms</h2>
        <EmptyState variant="no_data" title="Alarm totals not reported" />
      </section>
    );
  }
  const warn = t.warn ?? 0;
  const critical = t.critical ?? 0;
  if (warn === 0 && critical === 0) return null;

  return (
    <section className="sk-card">
      <h2 className="text-sm text-text-muted">Fleet alarms</h2>
      <div className="flex items-baseline gap-density-gap">
        {critical > 0 ? (
          <span className="flex items-center gap-1">
            <HealthBadge state="fault" showLabel={false} />
            <span className="font-semibold">{critical}</span>
            <span className="text-text-muted text-sm">critical</span>
          </span>
        ) : null}
        {warn > 0 ? (
          <span className="flex items-center gap-1">
            <HealthBadge state="warning" showLabel={false} />
            <span className="font-semibold">{warn}</span>
            <span className="text-text-muted text-sm">warn</span>
          </span>
        ) : null}
      </div>
      {/* Only stated when BOTH counts are known: "0 of 0 sites" printed as fact was
          an unmeasured pair, not a finding. */}
      {t.sites_with_alarms != null && t.sites_in_scope != null ? (
        <span className="text-text-muted text-xs">
          across {t.sites_with_alarms} of {t.sites_in_scope} sites
        </span>
      ) : null}
    </section>
  );
}


/* ════════════════════════════════════════════════════════════════════════
   Composition dispatch map
   ════════════════════════════════════════════════════════════════════════ */

const COMPOSITION_FN: Record<CompositionRole, (props: CompositionProps) => React.ReactNode> = {
  easy_m: (p) => (
    <HomeEasyM
      h={p.h}
      scopeLabel={p.scopeLabel}
      from={p.from}
      to={p.to}
      period={p.period}
      mode={p.mode}
      tenant={p.tenant}
      sites={p.sites}
      fleet={p.fleet as never}
      onPeriod={p.onPeriod}
      onWindow={p.onWindow}
      window={p.window}
    />
  ),
  executive: HomeExecutive,
  asset_manager: HomeAssetManager,
  regional_ops: HomeRegionalOps,
  control_room: HomeControlRoom,
  site: HomeSite,
  technician: HomeTechnician,
  contractor: HomeContractor,
  viewer: HomeViewer,
  ops: HomeOps,
};

/* ════════════════════════════════════════════════════════════════════════
   Main Home screen — data fetch + composition dispatch
   ════════════════════════════════════════════════════════════════════════ */

const SITE_SCOPED_ROLES = new Set(["site_engineer", "site_manager", "technician"]);

export function Home({ period, from, to, mode }: {
  period?: string; from?: string; to?: string; mode?: string;
}): React.ReactNode {
  const caps = useCapabilities();
  const navigate = useNavigate();
  // UX-REV-003 I1: default the period for the data calls. On first paint `period`
  // is briefly undefined, so /v1/home fired once as `…/home` and again as
  // `…/home?period=today` — two of a ~7s call. validateTimeSearch defaults period
  // to "today" anyway, so this only collapses that first-render race; it changes
  // no chosen window. (The ~7s latency of /v1/home itself is server-side.)
  const dataPeriod = period ?? "today";
  const home = useGetV1Home({ period: dataPeriod, from, to });
  const plants = useGetV1Plants({ period: dataPeriod, from, to });
  const hasLossCascade = useEntitlement("loss_cascade");

  const tenant = caps.scopes[0]?.id ?? "";
  const scopeLabel = caps.scopes[0]?.label ?? "";
  const hasValueLens = caps.lenses.some((l) => String(l).toLowerCase() === "value");

  // UX-002 wireframe 04 "P2 / H1 (S)": site-scoped roles see Site:Now as their home.
  const siteScopes = caps.scopes.filter((s) => s.type === "site");
  const siteSlug = siteScopes.length === 1 ? (siteScopes[0]?.id ?? null) : null;
  const isSiteHome = SITE_SCOPED_ROLES.has(caps.role ?? "") && siteSlug !== null;
  useEffect(() => {
    if (isSiteHome && siteSlug) {
      void navigate({
        to: `/t/${tenant}/s/${siteSlug}/plants/${siteSlug}/now` as never,
        search: (period ? { period } : {}) as never,
        replace: true,
      });
    }
  }, [isSiteHome, tenant, siteSlug, period, navigate]);

  if (isSiteHome) {
    return (
      <div className="sk-home" aria-busy="true">
        <Skeleton variant="text" width="12rem" />
      </div>
    );
  }

  if (home.isLoading) {
    return (
      <div className="sk-home" aria-busy="true">
        <div className="sk-kpistrip">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="tile" />
          ))}
        </div>
        <Skeleton variant="row" lines={6} />
      </div>
    );
  }

  if (home.isError || !home.data || home.data.status !== 200) {
    return (
      <ErrorState
        title="Home is unavailable"
        message="The home panel did not respond."
        onRetry={() => void home.refetch()}
      />
    );
  }

  const env = home.data.data;
  const h = env.data;
  const quiet = env.meta.quiet;
  const role = resolveRole(h.composition, caps.role);
  const sites = plants.data?.status === 200 ? (plants.data.data.data.items ?? []) : [];
  const fleet = plants.data?.status === 200
    ? (plants.data.data.data as unknown as { fleet?: unknown }).fleet
    : undefined;
  // The period lives in the URL, so a chosen window is shareable and the back
  // button works. Changing it drops from/to: they belong to a custom range only.
  // The server's own resolved window, which the chart draws rather than a window
  // the browser worked out from its clock.
  const win = home.data?.status === 200
    ? (home.data.data.meta as unknown as { period?: { start?: string; end?: string; start_at?: string; end_at?: string } }).period
    : undefined;
  const onWindow = (f: string, tt: string): void => {
    void navigate({ search: ((prev: Record<string, unknown>) => ({
      ...prev, period: "custom", from: f, to: tt,
    })) as never });
  };
  const onPeriod = (next: string): void => {
    void navigate({ search: ((prev: Record<string, unknown>) => ({
      ...prev, period: next, from: undefined, to: undefined,
    })) as never });
  };

  const props: CompositionProps = {
    h,
    period,
    from,
    to,
    hasValueLens,
    hasLossCascade,
    scopeLabel,
    sites,
    fleet,
    mode,
    onPeriod,
    onWindow,
    window: win,
    navigate,
    tenant,
    quiet,
  };

  const Composition = COMPOSITION_FN[role];
  // The Easy M composition carries its own alerts tile and its own by-site list,
  // both from the drawing. Appending the fleet alarms card here put a THIRD alert
  // count on the same screen, over a different window, contradicting the tile.
  // One quantity, one figure.
  return (
    <>
      {Composition(props)}
      {role === "easy_m" ? null : <FleetAlarmsSummary />}
    </>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Shared helpers
   ════════════════════════════════════════════════════════════════════════ */

const COMPOSITION_MAP: Record<string, CompositionRole> = {
  M: "easy_m",
  E: "executive",
  AM: "asset_manager",
  RO: "regional_ops",
  CR: "control_room",
  S: "site",
  CM: "contractor",
  V: "viewer",
  OPS: "ops",
};

const ROLE_MAP: Record<string, CompositionRole> = {
  executive: "executive",
  asset_manager: "asset_manager",
  lender_viewer: "viewer",
  regional_ops: "regional_ops",
  control_room: "control_room",
  site_manager: "site",
  site_engineer: "site",
  technician: "technician",
  contractor_manager: "contractor",
  solarkey_ops: "ops",
  tenant_admin: "ops",
  viewer: "viewer",
};

function resolveRole(composition: string | undefined, capsRole: string | null): CompositionRole {
  const r = (capsRole ?? "").toLowerCase();
  // The tier decides before the role does. A person's role still shapes every
  // other composition, but at Easy M the product IS the Easy M composition, and
  // only the server knows the tenant's tier.
  if (composition === "M") return "easy_m";
  if (ROLE_MAP[r]) return ROLE_MAP[r];
  if (composition && COMPOSITION_MAP[composition]) return COMPOSITION_MAP[composition];
  return "regional_ops";
}

function figVal(f?: Figure): number {
  return typeof f?.value === "number" ? f.value : -1;
}

function bestFigure(s: { energy_at_stake?: Figure; energy_today?: Figure }): Figure | undefined {
  return typeof s.energy_at_stake?.value === "number" ? s.energy_at_stake : s.energy_today;
}

function inlineFig(f?: Figure): React.ReactNode {
  return f
    ? <Metric figure={f} showProvenance={false} showFreshness={false} />
    : withheld("CANNOT_CHECK_FIGURE_ABSENT");
}

/**
 * The withheld treatment for a figure this panel renders in its own big-number
 * style: the figure's own state word and reason, or CANNOT_CHECK when the field
 * is absent from the payload altogether. Never a dash, never a zero (ruling §3).
 */
function withheldFig(f?: Figure, template = "CANNOT_CHECK_FIGURE_ABSENT"): React.ReactNode {
  return f
    ? <StateWord state={f.state} reason={f.reason} />
    // The state follows the template rather than being fixed, the same rule the
    // server applies. The two are not synonyms: CANNOT_CHECK is "we could not
    // look", NOT_MEASURED is "this is not measured" — and a fixed CANNOT_CHECK
    // beside a NOT_MEASURED_* reason contradicts itself in one breath.
    : <StateWord state={stateFor(template)} reason={{ template }} />;
}

function stateFor(template: string): Figure["state"] {
  if (template.startsWith("NOT_MEASURED")) return "NOT_MEASURED";
  if (template.startsWith("NOT_APPLICABLE")) return "NOT_APPLICABLE";
  if (template.startsWith("PROVISIONAL")) return "PROVISIONAL";
  return "CANNOT_CHECK";
}

function withheld(template: string): React.ReactNode {
  return <StateWord state="CANNOT_CHECK" reason={{ template }} />;
}

/**
 * Sites reporting, as a number only when the liveness read actually produced one.
 * A missing `sites_up` must never fall back to the site count — that would assert
 * every site is up precisely when the platform cannot tell.
 */
function sitesUpOf(h: { data_path?: { sites_up?: number } }): number | null {
  return typeof h.data_path?.sites_up === "number" ? h.data_path.sites_up : null;
}

/** The docket's time column: the detection's opened_at as a short local time. */
function docketWhen(when: unknown): string {
  if (typeof when !== "string" || !when) return "";
  const d = new Date(when);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function renderLossValue(rows: LossCascadeRow[], step: CascadeStep, hasValueLens: boolean): React.ReactNode {
  const row = rows.find((r) => (r.label ?? "—") === step.label);
  if (hasValueLens && row?.money) return <Money money={row.money} />;
  if (row?.figure) return <Metric figure={row.figure} />;
  return null;
}

function mapStatus(status?: RowStatus): CascadeRowStatus {
  if (status === "engine" || status === "platform_estimate" || status === "NOT_MEASURED") return status;
  return "NOT_MEASURED";
}

const ACTION_LABEL: Record<string, string> = {
  raise_work_order: "Raise work order",
  approve: "Approve",
  acknowledge: "Acknowledge",
  assign: "Assign",
  verify: "Verify",
  escalate: "Escalate",
  override: "Override",
  configure: "Configure",
  report: "Report",
  execute_command: "Execute",
  review: "Review",
};

function actionLabel(action: Action): string {
  return ACTION_LABEL[action as string] ?? String(action);
}

/** A figure's value and unit at the scale a reader wants — 75,849 kWh as
 *  "75.8 MWh", not "75.8 GWh".
 *
 *  These compositions divided by 1000 and hardcoded the label "GWh". The server
 *  sends energy in kWh, so that is MWh: every energy headline here read a
 *  thousand times too large, and a five-site estate appeared to deliver 75 GWh in
 *  a day. `atDisplayScale` carries the unit with the arithmetic, which is the
 *  whole point of it — it was already in use two screens over. */
function ScaledVal({ figure }: { figure?: Figure }): React.ReactNode {
  const f = atDisplayScale(figure) ?? figure;
  if (typeof f?.value !== "number") return withheldFig(figure);
  return <>{formatNum(f.value)}<small style={MET_UNIT}>{f.unit}</small></>;
}

/** The same scaling, as text, for the places that build a sentence. */
function scaledText(figure?: Figure): string {
  const f = atDisplayScale(figure) ?? figure;
  if (typeof f?.value !== "number") return "";
  return `${formatNum(f.value)} ${f.unit ?? ""}`.trim();
}

function formatNum(n: number): string {
  return n.toLocaleString("en-IN", { maximumFractionDigits: 1 });
}

/* ════════════════════════════════════════════════════════════════════════
   Inline styles — Sir's wireframe CSS tokens
   ════════════════════════════════════════════════════════════════════════ */

const CARD: React.CSSProperties = {
  padding: "14px 16px",
  borderRadius: "var(--sk-radius-card, 8px)",
  border: "1px solid var(--sk-color-border-1)",
  background: "var(--sk-color-surface-0)",
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const CARD_HEAD: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: 6,
  marginBottom: 2,
  flexWrap: "wrap",
};

const CARD_TITLE: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "var(--sk-color-ink-1)",
};

const CARD_SUB: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 400,
  color: "var(--sk-color-ink-3)",
};

const CARD_RIGHT: React.CSSProperties = {
  marginLeft: "auto",
  fontSize: 12,
  color: "var(--sk-color-ink-3)",
};

const H2_MUTED: React.CSSProperties = {
  fontSize: "0.875rem",
  fontWeight: 400,
  color: "var(--sk-text-muted)",
  margin: 0,
};

const ROW_13_1: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1.3fr 1fr",
  gap: 12,
  marginBottom: 12,
};

const ROW_14_1: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1.4fr 1fr",
  gap: 12,
  marginBottom: 12,
};

const ROW_2COL: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 16,
};

const ROW_3COL: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  gap: 16,
};

const ROW_4COL: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: 16,
};

const ROW_5COL: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(5, 1fr)",
  gap: 12,
  marginBottom: 12,
};

const MET_LABEL: React.CSSProperties = {
  fontSize: 11,
  color: "var(--sk-color-ink-3)",
  letterSpacing: "0.01em",
};

const MET_VAL: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 600,
  lineHeight: 1.15,
  letterSpacing: "-0.02em",
  color: "var(--sk-color-ink-1)",
};

const MET_VAL_BIG: React.CSSProperties = {
  fontSize: 32,
  fontWeight: 600,
  lineHeight: 1.15,
  letterSpacing: "-0.02em",
  color: "var(--sk-color-ink-1)",
};

const MET_UNIT: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 400,
  color: "var(--sk-color-ink-3)",
  marginLeft: 2,
};

const MET_SUB: React.CSSProperties = {
  fontSize: 11,
  color: "var(--sk-color-ink-3)",
  marginTop: 2,
};

const CHIP: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  border: "1px solid var(--sk-color-border-2)",
  borderRadius: 4,
  padding: "1px 6px",
  fontSize: 10,
};

const CHIP_BASIS: React.CSSProperties = {
  ...CHIP,
  borderColor: "transparent",
  background: "var(--sk-color-accent-soft)",
  color: "var(--sk-color-accent)",
  fontWeight: 600,
};

const BAR_TRACK: React.CSSProperties = {
  width: "100%",
  height: 6,
  borderRadius: 3,
  background: "var(--sk-color-surface-2)",
  overflow: "hidden",
};

const BAR_FILL: React.CSSProperties = {
  height: "100%",
  borderRadius: 3,
  background: "var(--sk-color-accent)",
};

const DOCKET_IT: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "48px 1fr auto",
  gap: 8,
  padding: "8px 0",
  borderBottom: "1px solid var(--sk-color-border-1)",
  alignItems: "start",
  fontSize: 12,
};

const DOCKET_WHEN: React.CSSProperties = {
  fontSize: 11,
  color: "var(--sk-color-ink-3)",
  textAlign: "right",
  paddingTop: 2,
};

const DOCKET_AMT: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  whiteSpace: "nowrap",
};

const BTN_SMALL: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "3px 10px",
  borderRadius: "var(--sk-radius-control, 6px)",
  border: "1px solid var(--sk-color-border-2)",
  background: "var(--sk-color-surface-0)",
  color: "var(--sk-color-ink-2)",
  fontSize: 11,
  fontWeight: 500,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const BTN_PRIMARY: React.CSSProperties = {
  ...BTN_SMALL,
  borderColor: "var(--sk-color-accent)",
  background: "var(--sk-color-accent)",
  color: "#fff",
  fontWeight: 600,
};

const TABLE: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 12,
};

const TH: React.CSSProperties = {
  padding: "6px 8px",
  borderBottom: "2px solid var(--sk-color-border-1)",
  textAlign: "left",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--sk-color-ink-3)",
  letterSpacing: "0.01em",
};

const TD: React.CSSProperties = {
  padding: "8px 8px",
  borderBottom: "1px solid var(--sk-color-border-1)",
  verticalAlign: "middle",
};

const TR: React.CSSProperties = {};
